import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { firstValueFrom } from 'rxjs';
import { LeaveService } from '../../services/leave.service';
import {
  AgentSuggestion,
  ApplyLeavePayload,
  HalfDayPeriod,
  LeaveBalance,
  LeaveRequest,
  LeaveType,
  LeaveTypeBalance,
  SystemRole
} from '../../interfaces/leave.interface';

type LeaveTab = 'request' | 'mine' | 'team';

interface BalanceCard {
  key: LeaveType;
  label: string;
  hint: string;
  tone: 'casual' | 'annual' | 'sick';
  data: LeaveTypeBalance;
}

interface AttachmentPreview {
  url: string;
  safeUrl: SafeResourceUrl;
  name: string;
  kind: 'image' | 'pdf' | 'file';
  local: boolean;
}

@Component({
  selector: 'app-leave',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './leave.component.html',
  styleUrls: ['./leave.component.css']
})
export class LeaveComponent implements OnInit, OnDestroy {
  @Output() backClick = new EventEmitter<void>();
  @ViewChild('attachmentInput') attachmentInput?: ElementRef<HTMLInputElement>;

  isLoading = false;
  isRefreshing = false;
  isSubmitting = false;
  actionRequestId: string | null = null;
  openingAttachmentId: string | null = null;
  error: string | null = null;

  systemRole: SystemRole = 'User';
  currentUserId: string | null = null;
  balance: LeaveBalance | null = null;
  balanceCards: BalanceCard[] = [];
  myRequests: LeaveRequest[] = [];
  teamRequests: LeaveRequest[] = [];

  activeTab: LeaveTab = 'request';
  leaveTypes: LeaveType[] = ['Casual', 'Annual', 'Sick'];
  form: ApplyLeavePayload = this.emptyForm();
  rejectTarget: LeaveRequest | null = null;
  rejectReason = '';

  /** Requests whose "why" panel is open. */
  openSuggestionIds = new Set<string>();
  private suggestionTimer: ReturnType<typeof setTimeout> | null = null;

  localPreviewUrl: string | null = null;
  attachmentViewer: AttachmentPreview | null = null;

  readonly trackBalanceCard = (_: number, card: BalanceCard) => card.key;
  readonly trackRequest = (_: number, req: LeaveRequest) => req.id;

  constructor(
    private leaveService: LeaveService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer
  ) {}

  get isAdmin(): boolean {
    return this.systemRole === 'Admin';
  }

  get pendingMineCount(): number {
    return this.myRequests.filter((r) => r.status === 'Pending').length;
  }

  get pendingTeamCount(): number {
    return this.teamRequests.filter(
      (r) =>
        r.status === 'Pending' &&
        !!this.currentUserId &&
        r.requesterUserId.toLowerCase() !== this.currentUserId.toLowerCase()
    ).length;
  }

  get estimatedDays(): number {
    if (!this.form.startDate) return 0;
    if (this.form.isHalfDay) return 0.5;
    if (!this.form.endDate) return 0;
    const start = new Date(this.form.startDate);
    const end = new Date(this.form.endDate);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end < start) return 0;
    return Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
  }

  get selectedBalance(): LeaveTypeBalance | null {
    if (!this.balance) return null;
    if (this.form.leaveType === 'Annual') return this.balance.annual;
    if (this.form.leaveType === 'Sick') return this.balance.sick;
    return this.balance.casual;
  }

  get attachmentName(): string | null {
    return this.form.attachment?.name ?? null;
  }

  get attachmentSizeLabel(): string | null {
    const file = this.form.attachment;
    if (!file) return null;
    if (file.size < 1024) return `${file.size} B`;
    if (file.size < 1024 * 1024) return `${(file.size / 1024).toFixed(1)} KB`;
    return `${(file.size / (1024 * 1024)).toFixed(1)} MB`;
  }

  get localPreviewIsImage(): boolean {
    return !!this.form.attachment?.type?.startsWith('image/');
  }

  ngOnInit(): void {
    this.reload();
  }

  ngOnDestroy(): void {
    this.clearSuggestionTimer();
    this.revokeLocalPreview();
    this.closeAttachmentViewer();
  }

  onBack(): void {
    this.backClick.emit();
  }

  setTab(tab: LeaveTab): void {
    this.activeTab = tab;
  }

  selectLeaveType(type: LeaveType): void {
    this.form.leaveType = type;
  }

  selectHalfDayPeriod(period: HalfDayPeriod): void {
    this.form.halfDayPeriod = period;
  }

  reload(): void {
    const hasContent = !!this.balance;
    this.isLoading = !hasContent;
    this.isRefreshing = hasContent;
    this.error = null;

    this.leaveService.getMySystemRole().subscribe({
      next: (roleRes) => {
        this.systemRole = roleRes.data?.systemRole === 'Admin' ? 'Admin' : 'User';
        this.currentUserId = roleRes.data?.userId ?? null;
        this.loadCoreData();
      },
      error: (err) => {
        this.isLoading = false;
        this.isRefreshing = false;
        this.error = err?.error?.message || 'Failed to load leave identity.';
      }
    });
  }

  onHalfDayChange(): void {
    if (this.form.isHalfDay) {
      this.form.endDate = this.form.startDate;
      if (!this.form.halfDayPeriod) this.form.halfDayPeriod = 'Morning';
    } else {
      this.form.halfDayPeriod = null;
    }
  }

  onStartDateChange(): void {
    if (this.form.isHalfDay) this.form.endDate = this.form.startDate;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.form.attachment = file;
    this.revokeLocalPreview();
    if (file?.type?.startsWith('image/')) {
      this.localPreviewUrl = URL.createObjectURL(file);
    }
  }

  clearAttachment(): void {
    this.form.attachment = null;
    this.revokeLocalPreview();
    if (this.attachmentInput?.nativeElement) {
      this.attachmentInput.nativeElement.value = '';
    }
  }

  previewLocalAttachment(): void {
    if (!this.form.attachment) return;
    if (this.localPreviewIsImage && this.localPreviewUrl) {
      this.attachmentViewer = {
        url: this.localPreviewUrl,
        safeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(this.localPreviewUrl),
        name: this.form.attachment.name,
        kind: 'image',
        local: true
      };
      return;
    }
    const url = URL.createObjectURL(this.form.attachment);
    const kind = this.form.attachment.type === 'application/pdf' ? 'pdf' : 'file';
    if (kind === 'file') {
      this.triggerDownload(url, this.form.attachment.name);
      URL.revokeObjectURL(url);
      return;
    }
    this.attachmentViewer = {
      url,
      safeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(url),
      name: this.form.attachment.name,
      kind,
      local: false
    };
  }

  async openRequestAttachment(request: LeaveRequest): Promise<void> {
    if (!request.hasAttachment) return;
    this.openingAttachmentId = request.id;
    try {
      const res = await firstValueFrom(this.leaveService.getAttachment(request.id));
      let blob = res.body;
      if (!blob || blob.size === 0) {
        this.toastr.error('Attachment could not be loaded.');
        return;
      }

      const fileName =
        request.attachmentFileName ||
        this.fileNameFromDisposition(res.headers.get('content-disposition')) ||
        'attachment';
      const headerType = (res.headers.get('content-type') || '').split(';')[0].trim().toLowerCase();
      const contentType = (blob.type || headerType || this.guessMimeFromName(fileName)).toLowerCase();

      // API errors sometimes arrive as 200-shaped blobs when proxies rewrite status; detect JSON errors.
      if (contentType.includes('application/json')) {
        const message = await this.readBlobError(blob);
        this.toastr.error(message || 'Failed to open attachment.');
        return;
      }

      blob = this.ensureBlobMimeType(blob, fileName, contentType);
      const url = URL.createObjectURL(blob);
      const kind = contentType.startsWith('image/') || /\.(png|jpe?g|gif|webp)$/i.test(fileName)
        ? 'image'
        : contentType.includes('pdf') || fileName.toLowerCase().endsWith('.pdf')
          ? 'pdf'
          : 'file';

      if (kind === 'file') {
        this.triggerDownload(url, fileName);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
        this.toastr.success(`Downloaded ${fileName}`);
      } else {
        this.attachmentViewer = {
          url,
          safeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(url),
          name: fileName,
          kind,
          local: false
        };
      }
    } catch (err: any) {
      const blobErr = err?.error instanceof Blob ? await this.readBlobError(err.error) : null;
      const statusHint =
        err?.status === 404
          ? 'Attachment was not found on the server.'
          : err?.status === 403
            ? 'You do not have permission to view this attachment.'
            : null;
      this.toastr.error(blobErr || statusHint || err?.message || 'Failed to open attachment.');
    } finally {
      this.openingAttachmentId = null;
    }
  }

  downloadViewerAttachment(): void {
    if (!this.attachmentViewer) return;
    this.triggerDownload(this.attachmentViewer.url, this.attachmentViewer.name);
  }

  closeAttachmentViewer(): void {
    if (this.attachmentViewer && !this.attachmentViewer.local) {
      URL.revokeObjectURL(this.attachmentViewer.url);
    }
    this.attachmentViewer = null;
  }

  remainingPercent(data: LeaveTypeBalance): number {
    if (!data.total) return 0;
    return Math.max(0, Math.min(100, (data.remaining / data.total) * 100));
  }

  submitApply(): void {
    if (!this.form.leaveType || !this.form.startDate || !this.form.endDate || !this.form.reason.trim()) {
      this.toastr.warning('Please fill leave type, dates, and reason.');
      return;
    }
    if (this.form.isHalfDay && !this.form.halfDayPeriod) {
      this.toastr.warning('Select morning or afternoon for half-day leave.');
      return;
    }

    this.isSubmitting = true;
    this.leaveService
      .apply({
        ...this.form,
        reason: this.form.reason.trim()
      })
      .subscribe({
        next: (res) => {
          this.isSubmitting = false;
          this.toastr.success(res.message || 'Leave request submitted.');
          this.form = this.emptyForm();
          this.clearAttachment();
          this.activeTab = 'mine';
          this.reload();
        },
        error: (err) => {
          this.isSubmitting = false;
          this.toastr.error(err?.error?.message || 'Failed to submit leave request.');
        }
      });
  }

  cancelRequest(request: LeaveRequest): void {
    if (!confirm('Cancel this pending leave request?')) return;
    this.actionRequestId = request.id;
    this.leaveService.cancel(request.id).subscribe({
      next: (res) => {
        this.actionRequestId = null;
        this.toastr.success(res.message || 'Leave cancelled.');
        this.reload();
      },
      error: (err) => {
        this.actionRequestId = null;
        this.toastr.error(err?.error?.message || 'Failed to cancel leave.');
      }
    });
  }

  approveRequest(request: LeaveRequest): void {
    this.actionRequestId = request.id;
    this.leaveService.approve(request.id).subscribe({
      next: (res) => {
        this.actionRequestId = null;
        this.toastr.success(res.message || 'Leave approved.');
        this.reload();
      },
      error: (err) => {
        this.actionRequestId = null;
        this.toastr.error(err?.error?.message || 'Failed to approve leave.');
      }
    });
  }

  openReject(request: LeaveRequest): void {
    this.rejectTarget = request;
    // When the agent suggested rejecting, start from its reason (the admin can edit it).
    const suggestion = request.agentSuggestion;
    this.rejectReason = suggestion?.decision === 'reject'
      ? (suggestion.suggestedRejectionReason || suggestion.reason || '')
      : '';
  }

  /** The agent's note on a request: its proposal while Pending, or the record that it approved the request itself. */
  suggestionOf(request: LeaveRequest): AgentSuggestion | null {
    const s = request.agentSuggestion ?? null;
    if (!s) return null;
    return request.status === 'Pending' || s.status === 'executed' ? s : null;
  }

  suggests(request: LeaveRequest, decision: 'approve' | 'reject'): boolean {
    const s = this.suggestionOf(request);
    return s?.status === 'ready' && s.decision === decision;
  }

  toggleSuggestion(request: LeaveRequest): void {
    if (this.openSuggestionIds.has(request.id)) this.openSuggestionIds.delete(request.id);
    else this.openSuggestionIds.add(request.id);
  }

  /**
   * While the agent is still reviewing (or a request is so new it may not have been picked up yet), look again every
   * few seconds. Stops when nothing is waiting, so a tenant with no agent never polls.
   */
  private scheduleSuggestionRefresh(): void {
    this.clearSuggestionTimer();
    const now = Date.now();
    const isWaiting = (r: LeaveRequest, note: { status: string } | null | undefined) => {
      if (r.status !== 'Pending') return false;
      if (note) return note.status === 'reviewing';
      return now - new Date(r.createdAt).getTime() < 5 * 60 * 1000;
    };
    const waiting =
      this.myRequests.some((r) => isWaiting(r, r.agentReview)) ||
      (this.isAdmin && this.teamRequests.some((r) => isWaiting(r, r.agentSuggestion)));
    if (!waiting) return;
    this.suggestionTimer = setTimeout(() => this.refreshTeamRequests(), 15000);
  }

  private clearSuggestionTimer(): void {
    if (this.suggestionTimer) clearTimeout(this.suggestionTimer);
    this.suggestionTimer = null;
  }

  /** Quietly reloads the lists the agent's notes live on (the requester's own, and the admin's team list). */
  private refreshTeamRequests(): void {
    this.leaveService.getMyRequests().subscribe({
      next: (res) => (this.myRequests = res.data ?? []),
      error: () => undefined
    });
    if (!this.isAdmin) {
      this.scheduleSuggestionRefresh();
      return;
    }
    this.leaveService.getTeamRequests().subscribe({
      next: (res) => {
        this.teamRequests = res.data ?? [];
        this.scheduleSuggestionRefresh();
      },
      error: () => this.scheduleSuggestionRefresh()
    });
  }

  closeReject(): void {
    this.rejectTarget = null;
    this.rejectReason = '';
  }

  confirmReject(): void {
    if (!this.rejectTarget) return;
    if (!this.rejectReason.trim()) {
      this.toastr.warning('Rejection reason is required.');
      return;
    }
    const id = this.rejectTarget.id;
    this.actionRequestId = id;
    this.leaveService.reject(id, this.rejectReason.trim()).subscribe({
      next: (res) => {
        this.actionRequestId = null;
        this.closeReject();
        this.toastr.success(res.message || 'Leave rejected.');
        this.reload();
      },
      error: (err) => {
        this.actionRequestId = null;
        this.toastr.error(err?.error?.message || 'Failed to reject leave.');
      }
    });
  }

  canReview(request: LeaveRequest): boolean {
    return (
      this.isAdmin &&
      request.status === 'Pending' &&
      !!this.currentUserId &&
      request.requesterUserId.toLowerCase() !== this.currentUserId.toLowerCase()
    );
  }

  isOwnPending(request: LeaveRequest): boolean {
    return (
      request.status === 'Pending' &&
      !!this.currentUserId &&
      request.requesterUserId.toLowerCase() === this.currentUserId.toLowerCase()
    );
  }

  initials(name: string): string {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return '?';
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  formatDate(value: string): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  }

  formatDateRange(req: LeaveRequest): string {
    const start = this.formatDate(req.startDate);
    if (req.isHalfDay) return `${start} · ${req.halfDayPeriod}`;
    if (req.startDate === req.endDate) return start;
    return `${start} → ${this.formatDate(req.endDate)}`;
  }

  statusClass(status: string): string {
    return `lv-chip lv-chip--${(status || '').toLowerCase()}`;
  }

  typeTone(type: string): string {
    return `lv-type lv-type--${(type || '').toLowerCase()}`;
  }

  private loadCoreData(): void {
    let pending = this.isAdmin ? 3 : 2;
    const done = () => {
      pending -= 1;
      if (pending > 0) return;
      this.isLoading = false;
      this.isRefreshing = false;
    };

    this.leaveService.getBalance().subscribe({
      next: (balRes) => {
        this.balance = balRes.data ?? null;
        this.syncBalanceCards();
        done();
      },
      error: (err) => {
        this.error = err?.error?.message || 'Failed to load leave balance.';
        done();
      }
    });

    this.leaveService.getMyRequests().subscribe({
      next: (myRes) => {
        this.myRequests = myRes.data ?? [];
        this.scheduleSuggestionRefresh();
        done();
      },
      error: (err) => {
        this.error = err?.error?.message || 'Failed to load your leave requests.';
        done();
      }
    });

    if (this.isAdmin) {
      this.leaveService.getTeamRequests().subscribe({
        next: (teamRes) => {
          this.teamRequests = teamRes.data ?? [];
          this.scheduleSuggestionRefresh();
          done();
        },
        error: (err) => {
          this.error = err?.error?.message || 'Failed to load team leave requests.';
          done();
        }
      });
    }
  }

  private syncBalanceCards(): void {
    if (!this.balance) {
      this.balanceCards = [];
      return;
    }
    this.balanceCards = [
      { key: 'Casual', label: 'Casual', hint: 'Short personal time', tone: 'casual', data: this.balance.casual },
      { key: 'Annual', label: 'Annual', hint: 'Planned vacation', tone: 'annual', data: this.balance.annual },
      { key: 'Sick', label: 'Sick', hint: 'Health & recovery', tone: 'sick', data: this.balance.sick }
    ];
  }

  private emptyForm(): ApplyLeavePayload {
    return {
      leaveType: 'Casual',
      startDate: '',
      endDate: '',
      isHalfDay: false,
      halfDayPeriod: null as HalfDayPeriod | null,
      reason: '',
      attachment: null
    };
  }

  private revokeLocalPreview(): void {
    if (this.localPreviewUrl) {
      URL.revokeObjectURL(this.localPreviewUrl);
      this.localPreviewUrl = null;
    }
  }

  private triggerDownload(url: string, fileName: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  private fileNameFromDisposition(header: string | null): string | null {
    if (!header) return null;
    const utf = /filename\*=UTF-8''([^;]+)/i.exec(header);
    if (utf?.[1]) return decodeURIComponent(utf[1]);
    const plain = /filename="?([^";]+)"?/i.exec(header);
    return plain?.[1] ?? null;
  }

  private async readBlobError(blob: Blob): Promise<string | null> {
    try {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      return parsed?.message || parsed?.Message || text || null;
    } catch {
      return null;
    }
  }

  private guessMimeFromName(fileName: string): string {
    const ext = fileName.split('.').pop()?.toLowerCase() || '';
    switch (ext) {
      case 'png':
        return 'image/png';
      case 'jpg':
      case 'jpeg':
        return 'image/jpeg';
      case 'gif':
        return 'image/gif';
      case 'webp':
        return 'image/webp';
      case 'pdf':
        return 'application/pdf';
      case 'txt':
        return 'text/plain';
      case 'doc':
        return 'application/msword';
      case 'docx':
        return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls':
        return 'application/vnd.ms-excel';
      case 'xlsx':
        return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      default:
        return 'application/octet-stream';
    }
  }

  private ensureBlobMimeType(blob: Blob, fileName: string, contentType: string): Blob {
    if (blob.type && blob.type !== 'application/octet-stream') return blob;
    const mime = contentType && contentType !== 'application/octet-stream'
      ? contentType
      : this.guessMimeFromName(fileName);
    return new Blob([blob], { type: mime });
  }
}
